import { useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { parseAxiosFormErrors } from "~/api/handleAxiosFieldErrors";
import {
  createInsole,
  INSOLE_ARCH_TYPE,
  INSOLE_GENDER,
  INSOLE_SIZES,
} from "~/api/insoles";
import { errorToast, useToastStore } from "~/components";
import { Button, Input, Listbox, Modal } from "~/ui";
import { getEnumValues } from "~/utils";

interface AddInsoleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddInsoleModal({ isOpen, onClose }: AddInsoleModalProps) {
  const { t } = useTranslation();
  const { pushToast } = useToastStore();

  const schema = z.object({
    serialNumber: z
      .string({ required_error: t("insoles.please_enter_serial_number") })
      .min(1, { message: t("insoles.please_enter_serial_number") }),
    gender: z.enum(getEnumValues(INSOLE_GENDER), {
      required_error: t("insoles.please_enter_insole_gender"),
    }),
    size: z.number({ required_error: t("insoles.please_enter_insole_size") }),
    archType: z.enum(getEnumValues(INSOLE_ARCH_TYPE), {
      required_error: t("insoles.please_enter_insole_arch_type"),
    }),
  });

  type InsoleFormData = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors },
  } = useForm<InsoleFormData>({
    resolver: zodResolver(schema),
  });

  const resetForm = useCallback(
    () =>
      reset({
        serialNumber: undefined,
        size: undefined,
        gender: undefined,
        archType: undefined,
      }),
    [reset],
  );

  const closeAndReset = () => {
    resetForm();
    onClose();
  };

  const { mutate, isSuccess: isSubmitSuccessful } = useMutation({
    mutationFn: createInsole,
    onSuccess: () => {
      onClose();
      void pushToast({
        type: "success",
        message: t("insoles.create_success"),
      });
    },
    onError: (err) => {
      const parsedErr = parseAxiosFormErrors(err);
      if (parsedErr?.["insoles.0.serial_number"]) {
        setError("serialNumber", {
          message: t("insoles.insole_already_exists"),
        });
      } else {
        errorToast(err);
      }
    },
  });

  useEffect(resetForm, [resetForm, isSubmitSuccessful]);

  const onSubmit = (data: InsoleFormData) => {
    mutate(data);
  };

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      onClose={closeAndReset}
      title={t("insoles.add_insole")}
    >
      <form
        className="flex w-full flex-col gap-3"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="serialNumber"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Input
              id="serialNumber"
              className="w-full"
              label={t("insoles.insole")}
              placeholder={t("insoles.serial_number")}
              error={errors.serialNumber?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />

        <Controller
          name="gender"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Listbox
              id="gender"
              label={t("insoles.gender")}
              className="w-full"
              placeholder={t("insoles.gender")}
              error={errors.gender?.message}
              value={value}
              onChange={onChange}
              options={Object.values(INSOLE_GENDER).map((gender) => ({
                value: gender,
                label: t(`insoles.${gender}`),
              }))}
            />
          )}
        />

        <Controller
          name="size"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Listbox
              id="size"
              label={t("insoles.size")}
              className="w-full"
              placeholder={t("insoles.size")}
              error={errors.size?.message}
              value={value}
              onChange={onChange}
              options={INSOLE_SIZES.map((size) => ({
                value: size,
                label: (size / 10).toFixed(1),
              }))}
            />
          )}
        />

        <Controller
          name="archType"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Listbox
              id="archType"
              label={t("insoles.arch_type")}
              placeholder={t("insoles.arch_type")}
              className="w-full"
              error={errors.archType?.message}
              value={value}
              onChange={onChange}
              options={Object.values(INSOLE_ARCH_TYPE).map((archType) => ({
                value: archType,
                label: t(`insoles.${archType}`),
              }))}
            />
          )}
        />

        <div className="mt-5 flex flex-row gap-3">
          <Button
            variant="outline"
            onClick={closeAndReset}
            text={t("general.cancel")}
            className="flex grow"
          />
          <Button
            type="submit"
            variant="filled"
            text={t("general.save")}
            className="flex grow"
          />
        </div>
      </form>
    </Modal>
  );
}
